/**
 * Cart drawer - minimal, native <dialog>-based.
 *
 * Listens for CartAddEvent / CartUpdateEvent to open or refresh itself.
 *
 * Refresh strategy: instead of letting the section renderer replace the
 * whole section (which would destroy the open <dialog>), we fetch the
 * section HTML manually and swap only the *inner* content of the dialog.
 * The <dialog> itself stays in the DOM, preserving the open state and
 * avoiding the close/reopen flash.
 *
 * Public API:
 *   <cart-drawer-component>
 *     .open()        - opens the drawer
 *     .close()       - closes the drawer
 *     .refresh()     - re-fetches and swaps the inner content
 */

import { Component } from "@theme/component";
import { CartAddEvent, CartUpdateEvent } from "@theme/events";

/**
 * @typedef {Object} CartDrawerRefs
 * @property {HTMLDialogElement} dialog
 *
 * @extends {Component<CartDrawerRefs>}
 */
class CartDrawerComponent extends Component {
  requiredRefs = ["dialog"];

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener(CartUpdateEvent.eventName, this.#handleCartUpdate);
    this.refs.dialog.addEventListener("click", this.#handleBackdropClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener(CartUpdateEvent.eventName, this.#handleCartUpdate);
  }

  /** Returns the parent <section class="shopify-section"> id (without prefix). */
  get sectionId() {
    const el = this.closest(".shopify-section");
    if (!el) throw new Error("<cart-drawer-component> must be inside a Shopify section");
    return el.id.replace(/^shopify-section-/, "");
  }

  /** Opens the drawer. */
  open() {
    if (!this.refs.dialog.open) this.refs.dialog.showModal();
  }

  /** Closes the drawer. */
  close() {
    if (this.refs.dialog.open) this.refs.dialog.close();
  }

  /**
   * Re-fetches the section HTML and swaps only the inner content of the
   * <dialog>, leaving the <dialog> element itself untouched.
   * This avoids closing the drawer during a refresh.
   */
  async refresh() {
    try {
      const url = new URL(window.location.href);
      url.searchParams.set("section_id", this.sectionId);

      const response = await fetch(url.toString());
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const html = await response.text();

      const fragment = new DOMParser().parseFromString(html, "text/html");
      const newInner = fragment.querySelector("[data-cart-drawer-inner]");
      const currentInner = this.querySelector("[data-cart-drawer-inner]");

      if (!newInner || !currentInner) {
        throw new Error("cart drawer inner content not found");
      }

      currentInner.replaceWith(newInner);
    } catch (error) {
      console.error("[cart-drawer] refresh failed:", error);
    }
  }

  /**
   * Triggered by add-to-cart forms after a successful POST to /cart/add.js.
   * Refreshes the drawer content, then opens it.
   */
  #handleCartAdd = async () => {
    await this.refresh();
    this.open();
  };

  /**
   * Triggered when items inside the drawer change quantity / are removed.
   * Refreshes only - preserves the existing open/closed state.
   */
  #handleCartUpdate = async () => {
    await this.refresh();
  };

  /**
   * Native <dialog> doesn't close on backdrop click out of the box.
   * We detect clicks landing on the dialog element itself (not its inner box).
   * @param {MouseEvent} event
   */
  #handleBackdropClick = (event) => {
    if (event.target === this.refs.dialog) this.close();
  };
}

if (!customElements.get("cart-drawer-component")) {
  customElements.define("cart-drawer-component", CartDrawerComponent);
}
